@if (impactReport) {
  <app-page-header [breadcrumbs]="breadcrumbs" />

  <!-- The report keeps the full width. When the page is wide enough, the
       simulation floats over its right edge and folds down to a tab;
       otherwise it becomes a bar stuck to the bottom of the page. -->
  <div class="relative py-4">
    <!-- Container blanc avec même largeur que les enfants -->
    <div
      id="container-white"
      class="mx-auto rounded-lg bg-white pb-8 md:max-w-5xl lg:max-w-7xl xl:max-w-[1400px] 2xl:max-w-[1800px]"
    >
      <div
        class="mb-6 h-[260px] w-full overflow-hidden rounded-lg bg-greyscale-200"
      >
        @if (impactReport.trip.imagePath) {
          <img
            [src]="impactReport.trip.imagePath"
            [alt]="impactReport.trip.title || 'Voyage'"
            class="h-full w-full object-cover"
          />
        } @else {
          <!-- Placeholder sobre -->
          <div class="flex h-full items-center justify-center pb-12">
            <app-greentripper-pin-icon
              styleClass="w-12 h-12 text-greyscale-400 ml-[7px]"
            />
          </div>
        }
      </div>

      <!-- Card Header et tous les composants - Largeur réduite de 30px de chaque côté -->
      <div class="space-y-8 px-2 md:px-6 xl:px-[36px]">
        <!-- Header Card -->
        <app-trip-impact-header
          [trip]="impactReport.trip"
          [countries]="impactReport.countries || []"
          [hasContributed]="false"
          [clients]="
            packLimitationService.canFilterClient()
              ? impactReport.clients || []
              : []
          "
          [logoSources]="getLogoSources()"
        >
        </app-trip-impact-header>

        <!-- Section Aperçu Général -->
        <app-impact-overview
          [tripImpact]="impactReport.trip.tripImpact || null"
          [fullImpactDetails]="impactReport.fullImpactDetails"
          [tripName]="impactReport.trip.title || ''"
          [tripId]="impactReport.trip.id || ''"
          [travelers]="impactReport.trip.travellers || 1"
          [company]="impactReport.company || null"
          [clients]="
            packLimitationService.canFilterClient()
              ? impactReport.clients || []
              : []
          "
          [showPercentage]="showPercentage"
          [excludeMainTransport]="excludeMainTransport"
          [groupTransports]="groupTransports"
          (showPercentageChange)="onShowPercentageChange($event)"
          (excludeMainTransportChange)="onExcludeMainTransportChange($event)"
          (groupTransportsChange)="onGroupTransportsChange($event)"
        >
        </app-impact-overview>

        <!-- Per-traveller objective and company commitments -->
        <app-trip-goals />

        @if (showNoTips()) {
          <section
            class="flex items-start gap-3 rounded-lg border border-greyscale-200 bg-primary-50 p-4"
            aria-labelledby="no-tips-title"
          >
            <lucide-icon
              name="lightbulb"
              [size]="18"
              class="mt-0.5 shrink-0 text-primary-600"
            />
            <div class="flex flex-col gap-1">
              <h2
                id="no-tips-title"
                class="text-paragraph-s-reg font-semibold text-primary-900"
              >
                {{ "reductionTips.empty.title" | transloco }}
              </h2>
              <p class="text-paragraph-s-reg text-greyscale-700">
                {{ "reductionTips.empty.description" | transloco }}
              </p>
            </div>
          </section>
        }

        <!-- Section Statistiques -->
        <section class="space-y-6">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-4">
              <h2 class="text-2xl font-bold text-[#293834]">
                {{ t("impact.stats.title") || "Statistiques" }}
              </h2>
              <!-- SelectButton pour mode Groupe/Individuel -->
              <div class="flex items-center gap-2">
                <p-selectButton
                  [options]="viewModeOptions"
                  [(ngModel)]="impactService.viewMode"
                  optionLabel="label"
                  optionValue="value"
                  [allowEmpty]="false"
                  styleClass="p-selectbutton-sm"
                  size="small"
                ></p-selectButton>
                <lucide-angular
                  [img]="infoIcon"
                  class="h-4 w-4 cursor-help text-greyscale-500"
                  pTooltip="{{ t('impact.viewMode.tooltip') }}"
                  tooltipPosition="top"
                ></lucide-angular>
              </div>
            </div>
            <button
              class="rounded-lg p-1 text-sm text-greyscale-500 transition-colors hover:bg-greyscale-50 hover:text-greyscale-700"
              (click)="toggleAllSections()"
              [pTooltip]="'impactReport.page.expandCollapse' | transloco"
              tooltipPosition="top"
            >
              <lucide-angular
                [img]="allSectionsOpen ? chevronsUpIcon : chevronsDownIcon"
                class="h-4 w-4"
              >
              </lucide-angular>
            </button>
          </div>

          <!-- Each category's tips sit under its results, outside the
               collapsible frame, so folding the results keeps them in view. -->
          <div class="flex flex-col gap-4">
            <div class="flex flex-col gap-2">
              <app-collapsible-section
                [title]="t('impact.categories.transport')"
                categoryType="transport"
                [isOpen]="allSectionsOpen"
                [chartData]="getTransportChartData()"
                [showPercentage]="showPercentage"
                [hasTimData]="hasTimData"
              >
              </app-collapsible-section>
              <app-reduction-tips-card section="transport" />
            </div>

            <div class="flex flex-col gap-2">
              <app-collapsible-section
                [title]="t('impact.categories.accommodation')"
                categoryType="accommodation"
                [isOpen]="allSectionsOpen"
                [chartData]="getAccommodationChartData()"
                [showPercentage]="showPercentage"
              >
              </app-collapsible-section>
              <app-reduction-tips-card section="accommodation" />
            </div>

            <div class="flex flex-col gap-2">
              <app-collapsible-section
                [title]="t('impact.categories.activities')"
                categoryType="activities"
                [isOpen]="allSectionsOpen"
                [chartData]="getActivitiesChartData()"
                [showPercentage]="showPercentage"
              >
              </app-collapsible-section>
              <app-reduction-tips-card section="activities" />
            </div>

            <app-collapsible-section
              [title]="t('impact.categories.food')"
              categoryType="food"
              [isOpen]="allSectionsOpen"
              [chartData]="getFoodChartData()"
              [showPercentage]="showPercentage"
            >
            </app-collapsible-section>

            <app-collapsible-section
              [title]="t('impact.categories.dailyImpact')"
              categoryType="daily"
              [isOpen]="allSectionsOpen"
              [chartData]="[]"
              [showPercentage]="showPercentage"
            >
              <app-impact-per-day-chart
                [dailyData]="dailyImpactData"
                [hasScheduledElements]="hasScheduledElements"
                [showPercentage]="showPercentage"
              >
              </app-impact-per-day-chart>
            </app-collapsible-section>
            <div class="flex flex-col gap-2">
              <app-eco-score
                [isOpen]="allSectionsOpen"
                [carbonIntensity]="
                  impactReport.trip.tripImpact?.carbonIntensity ?? 0
                "
              />
              <app-reduction-tips-card section="ecoScore" />
            </div>
          </div>
        </section>

        <!-- ADEME Équivalences -->
        @if (impactReport.ademeEquivalences && impactReport.impactCo2Meta) {
          <div class="mt-8">
            <app-ademe-equivalences
              [equivalences]="impactReport.ademeEquivalences"
              [meta]="impactReport.impactCo2Meta"
              [travellers]="
                impactService.perPerson() ? impactReport.trip.travellers || 1 : 1
              "
            >
            </app-ademe-equivalences>
          </div>
        }

        <!-- CTA Contribution -->
        <div class="mt-8">
          <app-contribution-cta
            #contributionCta
            [tripId]="impactReport.trip.id"
            [tripName]="impactReport.trip.title || ''"
            [co2Impact]="impactReport.trip.tripImpact?.impactPerTraveller || 0"
          >
          </app-contribution-cta>
        </div>
      </div>

      @if (showSimulation() && !hasRoomForSidePanel()) {
        <app-reduction-simulation-panel
          layout="bar"
          class="sticky bottom-0 z-20 mt-8 block"
          (reveal)="revealTips()"
        />
      }
    </div>

    @if (showSimulation() && hasRoomForSidePanel()) {
      <!-- A column as tall as the report, pinned to its right edge, for the
           panel to stick in while the page scrolls. The column lets clicks
           through: only the panel catches them. The panel is capped to the
           viewport, or a sticky element never shows what overflows it.
           Sticking counts from inside the page's 20px padding: −8px leaves
           12px above it, and the cap 12px below. -->
      <aside class="pointer-events-none absolute inset-y-4 right-0 z-30">
        <div
          class="pointer-events-auto sticky -top-2 max-h-[calc(100dvh-1.5rem)] overflow-y-auto rounded-lg shadow-[0_8px_24px_rgba(41,56,52,0.16)]"
        >
          <app-reduction-simulation-panel
            layout="side"
            (reveal)="revealTips()"
          />
        </div>
      </aside>
    }
  </div>

  <app-reduction-routes-dialog />
  <app-reduction-apply-dialog />
} @else {
  <!-- Loading state -->
  <div class="flex h-[calc(100dvh-200px)] items-center justify-center">
    <app-greentripper-loader size="xl"></app-greentripper-loader>
  </div>
}
